<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useWaCampaigns } from 'dashboard/composables/useWaCampaigns';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const { previewCampaign } = useWaCampaigns();

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  campaign: {
    type: Object,
    default: null,
  },
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'close']);

const isEdit = computed(() => !!props.campaign);

const form = ref({
  title: '',
  inbox_id: null,
  message_type: 'text',
  message_content: '',
  ai_rewrite_enabled: false,
  ai_rewrite_prompt: '',
  schedule_days: [],
  schedule_start_time: '08:00',
  schedule_end_time: '18:00',
  audience: [],
});

const imageFile = ref(null);
const imagePreview = ref(null);
const previewData = ref(null);
const previewLoading = ref(false);

const dayOptions = [
  { value: 1, label: 'OMNI.WA_CAMPAIGNS.DAYS_FULL.1' },
  { value: 2, label: 'OMNI.WA_CAMPAIGNS.DAYS_FULL.2' },
  { value: 3, label: 'OMNI.WA_CAMPAIGNS.DAYS_FULL.3' },
  { value: 4, label: 'OMNI.WA_CAMPAIGNS.DAYS_FULL.4' },
  { value: 5, label: 'OMNI.WA_CAMPAIGNS.DAYS_FULL.5' },
  { value: 6, label: 'OMNI.WA_CAMPAIGNS.DAYS_FULL.6' },
  { value: 0, label: 'OMNI.WA_CAMPAIGNS.DAYS_FULL.0' },
];

const MESSAGE_VARIABLES = [
  { variable: '{{contact_name}}', label: 'OMNI.WA_CAMPAIGNS.VARIABLES.CONTACT_NAME', icon: 'i-lucide-user' },
  { variable: '{{contact_phone}}', label: 'OMNI.WA_CAMPAIGNS.VARIABLES.CONTACT_PHONE', icon: 'i-lucide-phone' },
  { variable: '{{contact_email}}', label: 'OMNI.WA_CAMPAIGNS.VARIABLES.CONTACT_EMAIL', icon: 'i-lucide-mail' },
  { variable: '{{inbox_name}}', label: 'OMNI.WA_CAMPAIGNS.VARIABLES.INBOX_NAME', icon: 'i-lucide-inbox' },
  { variable: '{{account_name}}', label: 'OMNI.WA_CAMPAIGNS.VARIABLES.ACCOUNT_NAME', icon: 'i-lucide-building' },
  { variable: '{{date}}', label: 'OMNI.WA_CAMPAIGNS.VARIABLES.DATE', icon: 'i-lucide-calendar' },
  { variable: '{{time}}', label: 'OMNI.WA_CAMPAIGNS.VARIABLES.TIME', icon: 'i-lucide-clock' },
];

const isViperConnectInbox = inbox => {
  if (inbox.channel_type !== 'Channel::Api') return false;
  const attrs = inbox.additional_attributes || {};
  return attrs.is_viperconnect === true || attrs.evolution_instance_name != null;
};

// Get WhatsApp + ViperConnect/Evolution inboxes
const inboxes = computed(() => {
  return store.getters['inboxes/getInboxes'].filter(
    inbox => inbox.channel_type === 'Channel::Whatsapp' || isViperConnectInbox(inbox)
  );
});

// Get account labels
const labels = computed(() => {
  return store.getters['labels/getLabels'] || [];
});

const selectedLabelIds = ref([]);
const allContacts = ref(false);

const resetForm = () => {
  form.value = {
    title: '',
    inbox_id: null,
    message_type: 'text',
    message_content: '',
    ai_rewrite_enabled: false,
    ai_rewrite_prompt: '',
    schedule_days: [],
    schedule_start_time: '08:00',
    schedule_end_time: '18:00',
    daily_limit: 50,
    auto_finish: false,
    audience: [],
  };
  selectedLabelIds.value = [];
  allContacts.value = false;
  imageFile.value = null;
  imagePreview.value = null;
  previewData.value = null;
};

watch(() => props.isOpen, (newVal) => {
  if (!newVal) return;

  store.dispatch('inboxes/get');
  store.dispatch('labels/get');

  if (props.campaign) {
    form.value = {
      title: props.campaign.title,
      inbox_id: props.campaign.inbox_id,
      message_type: props.campaign.message_type,
      message_content: props.campaign.message_content,
      ai_rewrite_enabled: props.campaign.ai_rewrite_enabled,
      ai_rewrite_prompt: props.campaign.ai_rewrite_prompt || '',
      schedule_days: [...(props.campaign.schedule_days || [])],
      schedule_start_time: props.campaign.schedule_start_time,
      schedule_end_time: props.campaign.schedule_end_time,
      daily_limit: props.campaign.daily_limit || 50,
      auto_finish: props.campaign.auto_finish || false,
      audience: props.campaign.audience || [],
    };
    const audienceArr = props.campaign.audience || [];
    allContacts.value = audienceArr.some(a => a.type === 'AllContacts');
    selectedLabelIds.value = audienceArr
      .filter(a => a.type === 'Label')
      .map(a => a.id);
    if (props.campaign.image_url) {
      imagePreview.value = props.campaign.image_url;
    }
  } else {
    resetForm();
  }
}, { immediate: true });

const toggleDay = day => {
  const idx = form.value.schedule_days.indexOf(day);
  if (idx >= 0) {
    form.value.schedule_days.splice(idx, 1);
  } else {
    form.value.schedule_days.push(day);
  }
};

const toggleLabel = labelId => {
  const idx = selectedLabelIds.value.indexOf(labelId);
  if (idx >= 0) {
    selectedLabelIds.value.splice(idx, 1);
  } else {
    selectedLabelIds.value.push(labelId);
  }
};

const insertVariable = variableStr => {
  form.value.message_content += variableStr;
};

const handleImageChange = event => {
  const file = event.target.files[0];
  if (file) {
    imageFile.value = file;
    imagePreview.value = URL.createObjectURL(file);
  }
};

const removeImage = () => {
  imageFile.value = null;
  imagePreview.value = null;
};

const handlePreview = async () => {
  if (!isEdit.value) return;
  previewLoading.value = true;
  previewData.value = await previewCampaign(props.campaign.id);
  previewLoading.value = false;
};

const canSubmit = computed(() => {
  const hasAudience = allContacts.value || selectedLabelIds.value.length > 0;
  return (
    form.value.title &&
    form.value.inbox_id &&
    form.value.message_content &&
    form.value.schedule_days.length > 0 &&
    form.value.schedule_start_time &&
    form.value.schedule_end_time &&
    hasAudience
  );
});

const handleSubmit = () => {
  const audience = allContacts.value
    ? [{ type: 'AllContacts' }]
    : selectedLabelIds.value.map(id => ({ type: 'Label', id }));

  const data = new FormData();
  data.append('title', form.value.title);
  data.append('inbox_id', form.value.inbox_id);
  data.append('message_type', form.value.message_type);
  data.append('message_content', form.value.message_content);
  data.append('ai_rewrite_enabled', form.value.ai_rewrite_enabled);
  data.append('ai_rewrite_prompt', form.value.ai_rewrite_prompt);
  data.append('schedule_start_time', form.value.schedule_start_time);
  data.append('schedule_end_time', form.value.schedule_end_time);
  data.append('daily_limit', form.value.daily_limit);
  data.append('auto_finish', form.value.auto_finish);

  form.value.schedule_days.forEach(day => {
    data.append('schedule_days[]', day);
  });

  data.append('audience_json', JSON.stringify(audience));

  if (imageFile.value) {
    data.append('image', imageFile.value);
  }

  emit('save', data);
};
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="emit('close')"
  >
    <div class="bg-n-solid-2 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ isEdit ? t('OMNI.WA_CAMPAIGNS.FORM.EDIT_TITLE') : t('OMNI.WA_CAMPAIGNS.FORM.CREATE_TITLE') }}
        </h2>
        <button class="text-n-slate-10 hover:text-n-slate-12" @click="emit('close')">
          <span class="i-lucide-x size-5" />
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        <!-- Title -->
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
            {{ t('OMNI.WA_CAMPAIGNS.FORM.TITLE_LABEL') }}
          </label>
          <input
            v-model="form.title"
            type="text"
            :placeholder="t('OMNI.WA_CAMPAIGNS.FORM.TITLE_PLACEHOLDER')"
            class="w-full px-4 py-2.5 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          />
        </div>

        <!-- Inbox -->
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
            {{ t('OMNI.WA_CAMPAIGNS.FORM.INBOX_LABEL') }}
          </label>
          <select
            v-model="form.inbox_id"
            class="w-full px-4 py-2.5 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12"
          >
            <option :value="null" disabled>
              {{ t('OMNI.WA_CAMPAIGNS.FORM.INBOX_PLACEHOLDER') }}
            </option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
        </div>

        <!-- Message Type -->
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
            {{ t('OMNI.WA_CAMPAIGNS.FORM.MESSAGE_TYPE_LABEL') }}
          </label>
          <div class="flex gap-3">
            <button
              class="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors"
              :class="form.message_type === 'text'
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 text-woot-600 dark:text-woot-400'
                : 'border-n-weak bg-n-alpha-3 text-n-slate-11'"
              @click="form.message_type = 'text'"
            >
              <span class="i-lucide-type size-4" />
              {{ t('OMNI.WA_CAMPAIGNS.MESSAGE_TYPE.TEXT') }}
            </button>
            <button
              class="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors"
              :class="form.message_type === 'image_caption'
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 text-woot-600 dark:text-woot-400'
                : 'border-n-weak bg-n-alpha-3 text-n-slate-11'"
              @click="form.message_type = 'image_caption'"
            >
              <span class="i-lucide-image size-4" />
              {{ t('OMNI.WA_CAMPAIGNS.MESSAGE_TYPE.IMAGE_CAPTION') }}
            </button>
          </div>
        </div>

        <!-- Image Upload (if image_caption) -->
        <div v-if="form.message_type === 'image_caption'">
          <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
            {{ t('OMNI.WA_CAMPAIGNS.FORM.IMAGE_LABEL') }}
          </label>
          <div v-if="imagePreview" class="relative inline-block">
            <img :src="imagePreview" class="w-40 h-40 object-cover rounded-lg border border-n-weak" />
            <button
              class="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
              @click="removeImage"
            >
              <span class="i-lucide-x size-3.5" />
            </button>
          </div>
          <div v-else>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              class="block w-full text-sm text-n-slate-11 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-woot-50 file:text-woot-600 hover:file:bg-woot-100 dark:file:bg-woot-900/30 dark:file:text-woot-400"
              @change="handleImageChange"
            />
            <p class="text-xs text-n-slate-10 mt-1">
              {{ t('OMNI.WA_CAMPAIGNS.FORM.IMAGE_HINT') }}
            </p>
          </div>
        </div>

        <!-- Message -->
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
            {{ t('OMNI.WA_CAMPAIGNS.FORM.MESSAGE_LABEL') }}
          </label>
          <textarea
            v-model="form.message_content"
            rows="4"
            :placeholder="t('OMNI.WA_CAMPAIGNS.FORM.MESSAGE_PLACEHOLDER')"
            class="w-full px-4 py-2.5 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-2 focus:ring-woot-500 focus:border-transparent resize-none"
          />
          <!-- Variables -->
          <div class="mt-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
            <p class="text-xs font-medium text-emerald-700 dark:text-emerald-300 mb-2">
              {{ t('OMNI.WA_CAMPAIGNS.FORM.VARIABLES_LABEL') }}
            </p>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="varItem in MESSAGE_VARIABLES"
                :key="varItem.variable"
                type="button"
                class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md
                       bg-white dark:bg-emerald-900/40
                       text-emerald-700 dark:text-emerald-200
                       hover:bg-emerald-100 dark:hover:bg-emerald-800/60
                       border border-emerald-300 dark:border-emerald-700
                       shadow-sm hover:shadow
                       transition-all cursor-pointer"
                @click.prevent="insertVariable(varItem.variable)"
              >
                <span :class="[varItem.icon, 'size-3.5']" />
                {{ t(varItem.label) }}
              </button>
            </div>
          </div>
        </div>

        <!-- Schedule -->
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
            {{ t('OMNI.WA_CAMPAIGNS.FORM.SCHEDULE_DAYS_LABEL') }}
          </label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="day in dayOptions"
              :key="day.value"
              class="px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors"
              :class="form.schedule_days.includes(day.value)
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 text-woot-600 dark:text-woot-400'
                : 'border-n-weak bg-n-alpha-3 text-n-slate-11'"
              @click="toggleDay(day.value)"
            >
              {{ t(day.label) }}
            </button>
          </div>

          <div class="flex gap-4 mt-3">
            <div class="flex-1">
              <label class="block text-xs text-n-slate-10 mb-1">
                {{ t('OMNI.WA_CAMPAIGNS.FORM.START_TIME_LABEL') }}
              </label>
              <input
                v-model="form.schedule_start_time"
                type="time"
                class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12"
              />
            </div>
            <div class="flex-1">
              <label class="block text-xs text-n-slate-10 mb-1">
                {{ t('OMNI.WA_CAMPAIGNS.FORM.END_TIME_LABEL') }}
              </label>
              <input
                v-model="form.schedule_end_time"
                type="time"
                class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12"
              />
            </div>
          </div>
        </div>

        <!-- Daily Limit (per campaign) -->
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
            {{ t('OMNI.WA_CAMPAIGNS.FORM.DAILY_LIMIT_LABEL') }}
          </label>
          <input
            v-model.number="form.daily_limit"
            type="number"
            min="1"
            max="10000"
            class="w-32 px-4 py-2.5 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          />
          <p class="text-xs text-n-slate-10 mt-1">
            {{ t('OMNI.WA_CAMPAIGNS.FORM.DAILY_LIMIT_HINT') }}
          </p>
        </div>

        <!-- Auto Finish -->
        <div class="flex items-center justify-between p-3 bg-n-alpha-2 border border-n-weak rounded-lg">
          <div>
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('OMNI.WA_CAMPAIGNS.FORM.AUTO_FINISH_LABEL') }}
            </span>
            <p class="text-xs text-n-slate-10 mt-0.5">
              {{ t('OMNI.WA_CAMPAIGNS.FORM.AUTO_FINISH_HINT') }}
            </p>
          </div>
          <Switch v-model="form.auto_finish" />
        </div>

        <!-- Audience -->
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
            {{ t('OMNI.WA_CAMPAIGNS.FORM.AUDIENCE_LABEL') }}
          </label>
          <p class="text-xs text-n-slate-10 mb-2">
            {{ t('OMNI.WA_CAMPAIGNS.FORM.AUDIENCE_HINT') }}
          </p>

          <!-- All Contacts toggle -->
          <div class="flex items-center justify-between p-3 bg-n-alpha-2 border border-n-weak rounded-lg mb-3">
            <div>
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('OMNI.WA_CAMPAIGNS.FORM.ALL_CONTACTS_LABEL') }}
              </span>
              <p class="text-xs text-n-slate-10 mt-0.5">
                {{ t('OMNI.WA_CAMPAIGNS.FORM.ALL_CONTACTS_HINT') }}
              </p>
            </div>
            <Switch v-model="allContacts" />
          </div>

          <!-- Labels (only when not targeting all contacts) -->
          <div v-if="!allContacts">
            <p class="text-xs font-medium text-n-slate-10 mb-2">
              {{ t('OMNI.WA_CAMPAIGNS.FORM.FILTER_BY_LABELS') }}
            </p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="label in labels"
                :key="label.id"
                class="px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors"
                :class="selectedLabelIds.includes(label.id)
                  ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 text-woot-600 dark:text-woot-400'
                  : 'border-n-weak bg-n-alpha-3 text-n-slate-11'"
                @click="toggleLabel(label.id)"
              >
                {{ label.title }}
              </button>
            </div>
            <p v-if="labels.length === 0" class="text-xs text-n-slate-10 mt-1">
              {{ t('OMNI.WA_CAMPAIGNS.FORM.NO_LABELS') }}
            </p>
          </div>
        </div>

        <!-- AI Rewrite -->
        <div class="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-xl p-4">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <span class="i-lucide-sparkles size-5 text-violet-600 dark:text-violet-400" />
              <span class="text-sm font-medium text-violet-700 dark:text-violet-300">
                {{ t('OMNI.WA_CAMPAIGNS.FORM.AI_REWRITE_LABEL') }}
              </span>
            </div>
            <Switch v-model="form.ai_rewrite_enabled" />
          </div>
          <p class="text-xs text-violet-600/70 dark:text-violet-400/70 mb-3">
            {{ t('OMNI.WA_CAMPAIGNS.FORM.AI_REWRITE_HINT') }}
          </p>
          <div v-if="form.ai_rewrite_enabled">
            <label class="block text-xs font-medium text-violet-700 dark:text-violet-300 mb-1">
              {{ t('OMNI.WA_CAMPAIGNS.FORM.AI_PROMPT_LABEL') }}
            </label>
            <textarea
              v-model="form.ai_rewrite_prompt"
              rows="2"
              :placeholder="t('OMNI.WA_CAMPAIGNS.FORM.AI_PROMPT_PLACEHOLDER')"
              class="w-full px-3 py-2 border border-violet-200 dark:border-violet-700 rounded-lg bg-white dark:bg-violet-900/30 text-n-slate-12 placeholder:text-n-slate-10 text-sm resize-none"
            />
          </div>
        </div>

        <!-- Preview (only for edit) -->
        <div v-if="isEdit" class="border border-n-weak rounded-xl p-4">
          <div class="flex items-center justify-between mb-3">
            <span class="text-sm font-medium text-n-slate-11">
              {{ t('OMNI.WA_CAMPAIGNS.PREVIEW.TITLE') }}
            </span>
            <button
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-woot-600 bg-woot-50 dark:bg-woot-900/30 rounded-lg hover:bg-woot-100"
              :disabled="previewLoading"
              @click="handlePreview"
            >
              <span v-if="previewLoading" class="i-lucide-loader-2 size-3.5 animate-spin" />
              <span v-else class="i-lucide-eye size-3.5" />
              {{ t('OMNI.WA_CAMPAIGNS.FORM.PREVIEW_BTN') }}
            </button>
          </div>
          <div v-if="previewData" class="space-y-3">
            <div>
              <span class="text-xs font-medium text-n-slate-10">{{ t('OMNI.WA_CAMPAIGNS.PREVIEW.CONTACT') }}:</span>
              <span class="text-xs text-n-slate-11 ml-1">{{ previewData.contact?.name }} ({{ previewData.contact?.phone }})</span>
            </div>
            <div class="bg-n-alpha-3 rounded-lg p-3">
              <span class="text-xs font-medium text-n-slate-10 block mb-1">{{ t('OMNI.WA_CAMPAIGNS.PREVIEW.WITH_VARIABLES') }}</span>
              <p class="text-sm text-n-slate-12 whitespace-pre-wrap">{{ previewData.with_variables }}</p>
            </div>
            <div v-if="previewData.ai_rewritten" class="bg-violet-50 dark:bg-violet-900/20 rounded-lg p-3">
              <span class="text-xs font-medium text-violet-600 dark:text-violet-400 block mb-1">{{ t('OMNI.WA_CAMPAIGNS.PREVIEW.AI_REWRITTEN') }}</span>
              <p class="text-sm text-n-slate-12 whitespace-pre-wrap">{{ previewData.ai_rewritten }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak">
        <button
          class="px-4 py-2 text-sm font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-3"
          @click="emit('close')"
        >
          {{ t('OMNI.WA_CAMPAIGNS.FORM.CANCEL') }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!canSubmit || saving"
          @click="handleSubmit"
        >
          <span v-if="saving" class="i-lucide-loader-2 size-4 animate-spin inline-block mr-1" />
          {{ t('OMNI.WA_CAMPAIGNS.FORM.SAVE') }}
        </button>
      </div>
    </div>
  </div>
</template>
